import { computed, inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import type { AccountInfo } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { UserPermissions } from '@e-check/shared';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public msal = inject(MsalService);

  private _account = signal<AccountInfo | null>(null);
  public activeAccount = computed(() => {
    const acc = this._account();
    if (!acc) return null;

    const details = {
      email: acc.idTokenClaims?.['email'] as string | undefined,
    };
    return { ...acc, ...details };
  });
  public permissions = computed(
    () => (this.activeAccount()?.idTokenClaims?.roles ?? []) as UserPermissions[],
  );

  public hasPermission = (permission: UserPermissions) => this.permissions().includes(permission);

  public async loadConfigs(): Promise<null> {
    try {
      await this.msal.instance.initialize();
      const result = await this.msal.instance.handleRedirectPromise();
      if (window.location.hash.includes('code=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      if (result?.account) {
        this.msal.instance.setActiveAccount(result.account);
        this._account.set(result.account);
        return null;
      }
      const accounts = this.msal.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msal.instance.setActiveAccount(accounts[0]);
        this._account.set(accounts[0]);
        return null;
      }
      await this.msal.instance.loginRedirect({ scopes: environment.msal.scopes });
    } catch (e) {
      console.error('Auth init error:', e);
      if (window.location.hash.includes('code=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    return null;
  }

  getActiveAccount() {
    return this.msal.instance.getActiveAccount();
  }

  async login() {
    return this.msal.loginRedirect({
      scopes: environment.msal.scopes,
    });
  }

  async getToken(forceRefresh = false): Promise<string> {
    const account = this.getActiveAccount();

    if (!account) {
      await this.login();
      throw new Error('No account');
    }

    try {
      const result = await lastValueFrom(
        this.msal.acquireTokenSilent({
          scopes: environment.msal.scopes,
          account,
          forceRefresh,
        }),
      );

      return result.accessToken;
    } catch (e) {
      console.warn('Silent token failed → redirect');

      await lastValueFrom(
        this.msal.acquireTokenRedirect({
          scopes: environment.msal.scopes,
        }),
      );

      throw e;
    }
  }

  logout() {
    try {
      this.msal.logoutRedirect();
    } catch (error) {
      console.error(error);
    }
  }
}
